import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpClient } from '@angular/common/http';
import { ApiEndpoints } from '../../../shared/common/config';
import { User } from '../../../shared/interfaces/user.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { PermissionsList } from '../../../shared/common/PermissionList';
import { UserV2 } from '../interfaces/user';
import { environment } from 'src/environments/environment';
import { SocialUser } from '../interfaces/authentication.interface';
import { externalLogoutApiKeyService, externalStoreApiKeyService } from '@gozetta/dispatch-portal';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  jwtHelper = new JwtHelperService();

  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<any>;

  constructor(private readonly _http: HttpClient) {
    const storedUser = localStorage.getItem('currentUser');
    this.currentUserSubject = new BehaviorSubject<User>(
      storedUser ? JSON.parse(storedUser) : null,
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  getDispatchApiKey() {
   const currentUser = this.currentUserValue.user_profile;
   const apiKey = JSON.stringify({
      id: currentUser.id,
      email: currentUser.email,
      first_name: currentUser.first_name,
      full_name: currentUser.full_name,
      last_name: currentUser.last_name,
      access_token: currentUser.access_token,
      permissions: [],
      portal_type: 2,
      role: currentUser.role,
      consumer_identity: this.currentUserValue?.identity.client_id,
    });
    if(!localStorage.getItem('apiKey')) {
      externalStoreApiKeyService(apiKey);
    }
    
    return apiKey;
  }

  /**
   * check if user have access to a spacific page
   *
   */
  checkAccess(pagename: string): boolean {
    let userinfo = JSON.parse(localStorage.getItem('currentUser')!);
    if (
      userinfo?.user_profile.Permissions?.includes(
        PermissionsList[pagename as keyof typeof PermissionsList],
      )
    )
      return true;
    else return false;
  }

  /**
   * check if user LoggedIn
   *
   */
  checkIfLoggedIn() {
    if (!localStorage.getItem('currentUser')) {
      return false;
    }
    let currentUser = JSON.parse(localStorage.getItem('currentUser')!);
    const token = currentUser.user_profile?.access_token;

    if (token && !this.jwtHelper.isTokenExpired(token)) {
      // Token is present and not expired
      return true;
    }
    return false;
  }

  /**
   * login
   *
   */
  login(payload: any): Observable<any> {
    return this._http.post<any>(ApiEndpoints.authentication.login, payload);
  }

  /**
   * send verification code
   *
   */
  sendVerificationCode(email: string): Observable<any> {
    const url = `${ApiEndpoints.authentication.sendVerificationCode}?email=${email}`;
    return this._http.post<any>(url, null);
  }

  /**
   *  validate  verification code
   *
   */
  validateCode(email: string, verificationCode: string): Observable<any> {
    const url = ApiEndpoints.authentication.validate;
    const body = { email, verificationCode };

    return this._http.post<any>(url, body);
  }

  /**
   *  reset Password
   *
   */
  resetPassword(
    email: string,
    password: string,
    token: string,
  ): Observable<any> {
    const url = ApiEndpoints.authentication.reset;
    const body = { email, password, token };

    return this._http.post<any>(url, body);
  }

  /**
   * save user data
   *
   */
  saveUserData(userinfo: any) {
    localStorage.setItem('consumer_identity', userinfo.identity.client_id);
    localStorage.setItem('currentUser', JSON.stringify(userinfo));
  }

  /**
   * update user data
   *
   */

  updateUserData(updatedFields: any) {
    const storedUser = localStorage.getItem('currentUser');

    if (storedUser) {
      let parsedUser = JSON.parse(storedUser);

      // Update only the specified fields
      parsedUser.user_profile = {
        ...parsedUser.user_profile,
        ...updatedFields, // Update first_name, last_name, and email
      };

      localStorage.setItem('currentUser', JSON.stringify(parsedUser));
    }
  }

  /**
   * save user data
   *
   */
  removeUserData() {
    localStorage.setItem('currentUser', '');
    //for Dispatch API key
    externalLogoutApiKeyService() 
  }

  /**
   * getter: currentUserValue
   *
   */
  public get currentUserValue(): any {
    if (!localStorage.getItem('currentUser')) {
      return false;
    }
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem('currentUser')!),
    );
    this.currentUser = this.currentUserSubject.asObservable();
    return this.currentUserSubject.value;
  }

  public loggedIn() {
    return !!localStorage.getItem('currentUser');
  }

  saveUserDataV2(userinfo: UserV2) {
    localStorage.setItem('currentUser', JSON.stringify(userinfo));
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem('currentUser') ?? '{}'),
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  loginBySSO(body: SocialUser): Observable<any> {
    const apiBody = {
      Email: null,
      ClientName: body.clientName,
      IPAddress: '',
      Token: body.token,
      ProviderType: body.providerType,
    };

    return this._http.post<any>(
      `${environment.apiUrl}/client/authentication/customers-login-sso`,
      apiBody,
    );
  }
}
